import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useDialog } from '@/hooks/useDialog/useDialog'
import { Daycare } from '@/types/Daycare'
import { SlotCountPopup } from '../components/SlotCountField/slotCountField'
import ScheduleOccasionalSlot from '../../scheduleOccasionalSlot/scheduleOccasionalSlotDialog'
import { AnyConfirmPopup } from '../constants/blockByType'

interface UseOccasionalSlotConfirmationProps {
    popup?: AnyConfirmPopup
    slotCount: {
        value: number
        onChange: (value: number) => void
        min: number
        max: number
    }
    schedule: {
        value: string
        onChange: (value: string) => void
        maxDate: string
        daycare: Daycare
        onConfirm: () => void
    }
    onConfirm: () => void
}

export function useOccasionalSlotConfirmation({
    popup,
    slotCount,
    schedule,
    onConfirm
}: UseOccasionalSlotConfirmationProps) {
    const { t } = useTranslation()
    const { dialog, openDialog } = useDialog({
        dialogTitle: t('seatsNumber'),
        Component: SlotCountPopup,
        props: (closeDialog) => ({ ...slotCount, handleClose: closeDialog, onConfirm }),
        hideDialogClose: true
    })

    const { dialog: scheduleDialog, openDialog: openScheduleDialog } = useDialog({
        dialogTitle: t('scheduleOccasionalSlot'),
        Component: ScheduleOccasionalSlot,
        props: (closeDialog) => ({
            handleClose: closeDialog,
            scheduleDate: schedule.value,
            setScheduleDate: schedule.onChange,
            maxDate: schedule.maxDate,
            daycare: schedule.daycare,
            createOccasionalSlot: schedule.onConfirm
        }),
        hideDialogClose: true
    })

    const popups: Record<AnyConfirmPopup, { dialog: ReactNode; openDialog: () => void }> = {
        slotCount: { dialog, openDialog }
    }

    function handleFormConfirm(): void {
        if (popup) {
            popups[popup].openDialog()
        } else {
            onConfirm()
        }
    }

    return {
        confirmationDialog: popup ? popups[popup].dialog : null,
        handleFormConfirm,
        scheduleDialog,
        openScheduleDialog
    }
}
